import { z } from "zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { zodParse } from "../zodParse";
import { api, isApiError } from "..";

export const UserSchema = z.object({
  id: z.string().uuid(),
  email: z.string().email(),
  first_name: z.string().nullable(),
  last_name: z.string().nullable(),
  is_admin: z.boolean(),
  is_reviewer: z.boolean(),
  locale: z.string(),
});

export const LoggedInUserSchema = z.object({
  logged_in: z.literal(true),
  user: UserSchema,
  message: z.undefined(),
});

export const LoggedOutUserSchema = z.object({
  logged_in: z.literal(false),
  message: z.string(),
  user: z.undefined(),
});

export const UserLoginStatusSchema = z.discriminatedUnion("logged_in", [
  LoggedInUserSchema,
  LoggedOutUserSchema,
]);

export const ErrorSchema = z.object({
  message: z.string(),
  code: z.string().optional(),
});

export type LoggedInUser = z.infer<typeof LoggedInUserSchema>;
export type LoggedOutUser = z.infer<typeof LoggedOutUserSchema>;
export type UserLoginStatus = z.infer<typeof UserLoginStatusSchema>;
export type User = z.infer<typeof UserSchema>;
export type Error = z.infer<typeof ErrorSchema>;

// methods

async function getLoggedInUser(): Promise<UserLoginStatus | null> {
  try {
    const { data } = await api.get(`/users/me`);
    return zodParse(UserLoginStatusSchema, data);
  } catch (error) {
    if (isApiError(error)) {
      if (error.response?.status === 401) {
        return null;
      }
      throw new Error(error.response?.data?.message || "Failed to fetch user");
    }
    throw error;
  }
}

async function updateUser(id: User["id"], user: Partial<User>): Promise<Pick<User, "id">> {
  try {
    const { data } = await api.put(`/users/${id}`, user);
    return zodParse(UserSchema, data);
  } catch (error) {
    if (isApiError(error)) {
      throw new Error(error.response?.data?.message || "Failed to update user");
    }
    throw error;
  }
}

async function updateUserLocale(locale: string): Promise<{ locale: string }> {
  const { data } = await api.patch(`/users/me/locale`, { locale });
  return data;
}

// Hooks
export type UpdateUserParams = Omit<Partial<User>, "id" | "email"> & Pick<User, "id">;

export function useUpdateUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ...user }: UpdateUserParams) => updateUser(id, user),
    onSuccess: () => {
      // Invalidate the cached user data to trigger a refetch
      queryClient.invalidateQueries({ queryKey: ["user", "me"] });
    },
  });
}

export function useUpdateUserLocale() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (locale: string) => updateUserLocale(locale),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["user", "me"] });
    },
  });
}

export function useLoggedInUser() {
  return useQuery({
    queryKey: ["user", "me"],
    queryFn: () => getLoggedInUser(),
  });
}
